/**
 * Email Domain validation module for The Events Calendar attendee registration form.
 *
 * Checks, on form submit, that each attendee email address has a domain that can actually
 * receive mail (valid MX record, falling back to A/AAAA) via a REST API endpoint that wraps
 * PHP's checkdnsrr(). Blocks submission until every email domain on the form has been verified.
 */

declare const wp: any;
declare const crscNscData: { nonce: string };

const VALIDATED_FLAG = 'crscEmailDomainValidated';
const DOMAIN_OVERRIDE_CLASS = 'crsc-override-email-domain-checkbox';
const REGISTRATION_FORM_ID = 'tribe-tickets__registration-form';

export const initEmailDomainValidation = () => {
	document.addEventListener( 'submit', handleFormSubmit, true );
};

/**
 * Adds, at the end of the given registration form's footer, a checkbox that lets the customer
 * override the email domain verification error if they're confident there are no typos in the
 * attendees' email domains. The flagged domain is included (and bolded) in the label so the
 * customer understands exactly which domain they're confirming actually exists.
 *
 * This is only injected once the customer has actually been shown the domain verification error,
 * so they aren't presented with an override option before it is relevant to them.
 */
const ensureEmailDomainOverrideCheckbox = ( form: HTMLFormElement, domain: string ) => {
	const footer = form.querySelector<HTMLElement>( '.tribe-tickets__registration-footer' );

	if ( ! footer || footer.querySelector( `.${DOMAIN_OVERRIDE_CLASS}` ) ) return;

	const submitButton = footer.querySelector( '.tribe-tickets__registration-submit' );

	const containerDiv = document.createElement( 'div' );
	containerDiv.className = 'crsc-override-email-domain-container';
	containerDiv.style.width = '100%';
	containerDiv.style.margin = '10px 0';

	const checkbox = document.createElement( 'input' );
	checkbox.type = 'checkbox';
	checkbox.className = DOMAIN_OVERRIDE_CLASS;
	checkbox.id = `crsc-override-email-domain-${Math.random().toString( 36 ).slice( 2, 9 )}`;
	checkbox.style.marginRight = '5px';

	const label = document.createElement( 'label' );
	label.htmlFor = checkbox.id;
	label.appendChild( document.createTextNode( "I'm confident the attendee email addresses don't contain any typos and the domain " ) );

	const domainStrong = document.createElement( 'strong' );
	domainStrong.textContent = domain;
	label.appendChild( domainStrong );

	label.appendChild( document.createTextNode( ' exists.' ) );

	containerDiv.appendChild( checkbox );
	containerDiv.appendChild( label );

	if ( submitButton ) {
		footer.insertBefore( containerDiv, submitButton );
	} else {
		footer.appendChild( containerDiv );
	}

	// Clear any existing domain errors right away if the customer opts to override the check.
	checkbox.addEventListener( 'change', () => {
		if ( ! checkbox.checked ) {
			return;
		}

		form.querySelectorAll<HTMLInputElement>( '[name*="[email]"]' ).forEach( ( input ) => {
			input.setCustomValidity( '' );
			removeError( input );
		} );
	} );
};

/**
 * Determines whether the email domain override checkbox has been checked within the given form.
 */
const isEmailDomainOverrideChecked = ( form: HTMLFormElement ): boolean => {
	const checkbox = form.querySelector<HTMLInputElement>( `.${DOMAIN_OVERRIDE_CLASS}` );
	return !! checkbox?.checked;
};

const handleFormSubmit = ( event: Event ) => {
	const form = event.target as HTMLFormElement;

	if ( ! form || 'FORM' !== form.tagName ) {
		return;
	}

	if ( form.id !== REGISTRATION_FORM_ID ) {
		return;
	}

	const emailInputs = form.querySelectorAll<HTMLInputElement>( '[name*="[email]"]' );
	if ( emailInputs.length === 0 ) {
		return;
	}

	// If we've already validated this exact submission, let it through.
	if ( form.dataset[ VALIDATED_FLAG ] === 'true' ) {
		delete form.dataset[ VALIDATED_FLAG ];
		return;
	}

	// The customer has opted to bypass the domain verification check; let the submission proceed.
	if ( isEmailDomainOverrideChecked( form ) ) {
		emailInputs.forEach( ( input ) => {
			input.setCustomValidity( '' );
			removeError( input );
		} );
		return;
	}

	// Stop the native submission (and any framework submit handlers) until validation completes.
	event.preventDefault();
	event.stopPropagation();
	if ( typeof ( event as any ).stopImmediatePropagation === 'function' ) {
		( event as any ).stopImmediatePropagation();
	}

	validateFormEmails( form, emailInputs );
};

const validateFormEmails = async ( form: HTMLFormElement, emailInputs: NodeListOf<HTMLInputElement> ) => {
	const submitButton = form.querySelector<HTMLButtonElement>( '.tribe-tickets__registration-submit' );
	const originalButtonText = submitButton?.textContent ?? null;

	if ( submitButton ) {
		submitButton.disabled = true;
		submitButton.textContent = 'Validating email addresses…';
	}

	const uniqueEmails = Array.from( new Set(
		Array.from( emailInputs )
			.map( ( input ) => input.value.trim() )
			.filter( ( value ) => value.length > 0 )
	) );

	const results = await Promise.all(
		uniqueEmails.map( ( email ) => checkEmailDomain( email ) )
	);

	const invalidEmails = new Map<string, string>();
	uniqueEmails.forEach( ( email, index ) => {
		const result = results[ index ];
		if ( ! result.valid ) {
			invalidEmails.set( email.toLowerCase(), result.message );
		}
	} );

	let hasInvalidEmail = false;
	let firstInvalidInput: HTMLInputElement | null = null;

	emailInputs.forEach( ( input ) => {
		const value = input.value.trim().toLowerCase();
		const message = value ? invalidEmails.get( value ) : undefined;

		if ( message ) {
			hasInvalidEmail = true;
			input.setCustomValidity( message );
			showError( input, message );
			if ( ! firstInvalidInput ) {
				firstInvalidInput = input;
			}
		} else {
			input.setCustomValidity( '' );
			removeError( input );
		}
	} );

	if ( submitButton ) {
		submitButton.disabled = false;
		if ( null !== originalButtonText ) {
			submitButton.textContent = originalButtonText;
		}
	}

	if ( hasInvalidEmail ) {
		const invalidValue = ( firstInvalidInput as HTMLInputElement | null )?.value.trim() ?? '';
		const invalidDomain = invalidValue.slice( invalidValue.lastIndexOf( '@' ) + 1 );
		ensureEmailDomainOverrideCheckbox( form, invalidDomain );
		( firstInvalidInput as HTMLInputElement | null )?.focus();
		return;
	}

	// All emails passed validation, resubmit the form and let this listener step aside.
	form.dataset[ VALIDATED_FLAG ] = 'true';

	if ( typeof form.requestSubmit === 'function' ) {
		form.requestSubmit();
	} else {
		form.submit();
	}
};

const checkEmailDomain = async ( email: string ): Promise<{ valid: boolean; message: string }> => {
	try {
		const response = await wp.apiFetch( {
			path: 'crsc-events/v1/validate-email-domain',
			method: 'POST',
			data: { email },
			headers: {
				'X-WP-Nonce': crscNscData?.nonce,
			},
			credentials: 'include',
		} );

		const domain = email.slice( email.lastIndexOf( '@' ) + 1 );

		return {
			valid: !! response?.valid,
			message: response?.message || `Please check the email addresses of the attendees for typos. We cannot send a message to the address "${ domain }" because "${ domain }" is not active or cannot receive mail.`,
		};
	} catch ( error ) {
		// If the check itself fails (e.g. network issue), don't block the attendee from submitting.
		return { valid: true, message: '' };
	}
};

const showError = ( input: HTMLInputElement, message: string ) => {
	let error = input.parentNode?.querySelector( '.crsc-email-domain-error' );
	if ( ! error ) {
		error = document.createElement( 'div' );
		error.className = 'crsc-email-domain-error';
		error.setAttribute( 'style', 'color: #d63638; font-size: 14px; margin-top: 5px; margin-bottom: 5px; font-weight: 600;' );
		input.parentNode?.insertBefore( error, input );
	}
	error.textContent = message;
	input.style.borderColor = '#d63638';
};

const removeError = ( input: HTMLInputElement ) => {
	const error = input.parentNode?.querySelector( '.crsc-email-domain-error' );
	if ( error ) {
		error.remove();
	}
	input.style.borderColor = '';
};
